import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { _driver as cordovaSQLiteDriver } from 'localforage-cordovasqlitedriver';

export interface CachedAppConfig {
  configs: any;
  settings: any;
  languages: any;
  templates: any;
  templatesElements: any;
  timestamp: any;
  designName: string;
}

const CACHE_KEY = 'app-config-cache-v1';
const LEGACY_KEYS = [
  'appDesignConfigs', 'appDesignSettings', 'appDesignLanguages',
  'appDesignTemplates', 'appDesignTemplatesElements', 'appDesignTS', 'appDesignName'
];

@Injectable({ providedIn: 'root' })
export class AppConfigCacheService {
  lastReadSource: 'cache' | 'legacy' | 'empty' = 'empty';

  constructor(private storage: Storage, private platform: Platform) {}

  async read(): Promise<CachedAppConfig | undefined> {
    await this.ensureDriver();
    const value = await this.storage.get(CACHE_KEY);
    return this.isValid(value) ? value : undefined;
  }

  async readOrMigrateLegacy(): Promise<CachedAppConfig | undefined> {
    const cached = await this.read();
    if (cached) {
      this.lastReadSource = 'cache';
      return cached;
    }

    const legacy = this.readLegacy();
    if (!legacy) {
      this.lastReadSource = 'empty';
      return undefined;
    }

    await this.write(legacy);
    LEGACY_KEYS.forEach(key => localStorage.removeItem(key));
    this.lastReadSource = 'legacy';
    return legacy;
  }

  async write(value: CachedAppConfig): Promise<void> {
    if (!this.isValid(value)) {
      throw new Error('Refusing to cache an incomplete application configuration.');
    }
    await this.ensureDriver();
    await this.storage.set(CACHE_KEY, value);
    if (!this.isValid(await this.storage.get(CACHE_KEY))) {
      throw new Error('Application configuration cache verification failed.');
    }
  }

  async clear(): Promise<void> {
    await this.ensureDriver();
    await this.storage.remove(CACHE_KEY);
    LEGACY_KEYS.forEach(key => localStorage.removeItem(key));
  }

  async driver(): Promise<string | null> {
    await this.storage.ready();
    return this.storage.driver;
  }

  private async ensureDriver(): Promise<void> {
    await this.platform.ready();
    const driver = await this.driver();
    if (this.platform.is('cordova') && driver !== cordovaSQLiteDriver) {
      throw new Error(
        `Native configuration cache requires SQLite driver ${cordovaSQLiteDriver}, received ${driver ?? 'none'}.`
      );
    }
  }

  private readLegacy(): CachedAppConfig | undefined {
    try {
      const configs = this.parseLegacy('appDesignConfigs');
      const settings = this.parseLegacy('appDesignSettings');
      if (!configs || !settings) {
        return undefined;
      }
      return {
        configs,
        settings,
        languages: this.parseLegacy('appDesignLanguages') ?? {},
        templates: this.parseLegacy('appDesignTemplates') ?? {},
        templatesElements: this.parseLegacy('appDesignTemplatesElements') ?? {},
        timestamp: this.parseLegacy('appDesignTS') ?? 0,
        designName: this.parseLegacy('appDesignName') ?? '',
      };
    } catch (_) {
      return undefined;
    }
  }

  private parseLegacy(key: string): any {
    const raw = localStorage.getItem(key);
    return raw && raw !== 'undefined' ? JSON.parse(raw) : undefined;
  }

  private isValid(value: any): value is CachedAppConfig {
    return !!value && typeof value === 'object'
      && !!value.configs && typeof value.configs === 'object'
      && !!value.settings && typeof value.settings === 'object'
      && typeof value.designName === 'string';
  }
}
